import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Loader2, Headset } from "lucide-react";
import { API, LogoMark } from "@/lib/site";

const getVisitorId = () => {
  let id = localStorage.getItem("wynova_visitor_id");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("wynova_visitor_id", id);
  }
  return id;
};

const WELCOME = {
  message_id: "welcome",
  role: "ai",
  text: "Hi, I'm Nova from Wynova Systems. Ask me anything about our marketing, websites, chatbots, booking systems, SEO or hosting — or leave your details and the team will call you back.",
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([WELCOME]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState("ai");
  const bottomRef = useRef(null);
  const visitorId = useRef(getVisitorId()).current;

  const load = useCallback(async () => {
    if (!session) return;
    try {
      const { data } = await axios.get(`${API}/chat/${session.session_id}/messages`, {
        params: { visitor_id: visitorId },
      });
      setMode(data.mode);
      setMessages([WELCOME, ...data.messages]);
    } catch { /* keep polling silently */ }
  }, [session, visitorId]);

  useEffect(() => {
    if (!open || session) return;
    axios.post(`${API}/chat/start`, { visitor_id: visitorId })
      .then(({ data }) => setSession(data))
      .catch(() => {});
  }, [open, session, visitorId]);

  useEffect(() => {
    if (!open || !session) return;
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [open, session, load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !session || sending) return;
    setSending(true);
    const msg = text.trim();
    setText("");
    try {
      await axios.post(`${API}/chat/${session.session_id}/messages`, { visitor_id: visitorId, text: msg });
      await load();
      if (mode === "ai") setTimeout(load, 4000);
    } catch { /* ignore */ }
    setSending(false);
  };

  return (
    <>
      <button onClick={() => setOpen(!open)} data-testid="chat-open-button" aria-label="Open chat"
              className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-primary text-white shadow-2xl shadow-blue-600/40 flex items-center justify-center hover:scale-110 transition-transform">
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
      {open && (
        <div data-testid="chat-panel"
             className="fixed bottom-24 right-4 sm:right-5 z-50 w-[calc(100vw-2rem)] max-w-sm rounded-3xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl flex flex-col overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-4 bg-slate-950 text-white">
            <LogoMark className="w-8 h-8" />
            <div className="flex-1">
              <p className="font-display font-bold text-sm">Wynova Support</p>
              <p className="text-xs text-slate-400 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                {mode === "ai" ? "Nova · AI assistant" : "Live support"}
              </p>
            </div>
            {mode === "human" && <Headset className="w-4 h-4 text-cyan-400" />}
          </div>
          <div data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3 max-h-96 min-h-64">
            {messages.map((m) => (
              <div key={m.message_id} className={`flex ${m.role === "visitor" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                  m.role === "visitor"
                    ? "bg-primary text-white rounded-br-md"
                    : m.role === "agent"
                      ? "bg-slate-900 dark:bg-slate-700 text-white rounded-bl-md"
                      : "bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-bl-md"
                }`}>
                  {m.role === "agent" && <p className="text-[10px] font-bold uppercase tracking-wider text-cyan-400 mb-1">Wynova Support</p>}
                  {m.text}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <form onSubmit={send} className="flex items-center gap-2 px-3 py-3 border-t border-slate-200 dark:border-slate-800">
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…"
                   data-testid="chat-input" maxLength={2000}
                   className="flex-1 px-4 py-2.5 rounded-full bg-slate-100 dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary/60" />
            <button type="submit" disabled={sending || !text.trim() || !session} data-testid="chat-send-button" aria-label="Send message"
                    className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center hover:bg-blue-700 disabled:opacity-50 transition-colors">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
};
